// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import BackgroundContainer from 'presentations/views/components/organisms/service/BackgroundContainer';
import styled from '@emotion/styled';
import {
  signUpPath,
  dashboardPath,
  webPath,
  enterpriseInquiryPath,
  newsListPath,
  downloadsPath,
} from 'presentations/routes/path';
import { useRouter } from 'next/router';
import {
  borderRadius,
  fontSize,
  fontWeight,
  isMediaMax,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Icon from 'presentations/views/components/atoms/Icon';
import Button from 'presentations/views/components/atoms/Button';
import {
  black,
  gray01,
  gray04,
  primary,
  whiteFix,
} from 'presentations/views/styles/color';
import Link from 'presentations/views/components/atoms/Link';
import { ReactNode } from 'react';
import LoginButton from 'presentations/views/components/atoms/LoginButton';
import Image from 'presentations/views/components/atoms/Image';
import SmallSignUpButton from 'presentations/views/components/atoms/SmallSignUpButton';
import { Provider } from 'domains/models/account/providerType';
import { BlogListItem } from 'presentations/api/schema/contents';
import { ViewerItem } from 'presentations/api/schema/user';

interface Props {
  blog?: BlogListItem | null;
  children?: ReactNode;
  title: string;
  size?: 'large' | 'medium';
  description: string;
  onClickInquiry?: () => void;
  isShowSignUp?: boolean;
  viewer: ViewerItem | null;
  image?: {
    width?: number;
    height?: number;
    url: string;
  };
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const isMobile = isMediaMax('tablet');
  const size = props.size || 'medium';

  return (
    <ServiceMainVisual>
      <Title size={size}>{props.title}</Title>
      <Description size={size}>{props.description}</Description>
      {props.blog && (
        <NewContent>
          <NewMark>New</NewMark>
          <NewTitle href={newsListPath()}>{props.blog.title}</NewTitle>
          <NewLinkIcon name="chevron-right" />
        </NewContent>
      )}
      {props.isShowSignUp &&
        (!props.viewer ? (
          <>
            <SignUpText>{t('signup')}</SignUpText>
            <ButtonList>
              <ButtonListItem>
                <SignUpButtonContainer>
                  {isMobile ? (
                    <MobileSignUpButton
                      service={Provider.Google}
                      href={signUpPath({
                        provider: Provider.Google,
                        to: webPath(dashboardPath(), router.locale),
                        hiddenPasswordProvider: 1,
                      })}
                    />
                  ) : (
                    <SignUpButton
                      variant="signup"
                      service={Provider.Google}
                      isServiceOnly
                      href={signUpPath({
                        provider: Provider.Google,
                        to: webPath(dashboardPath(), router.locale),
                        hiddenPasswordProvider: 1,
                      })}
                    />
                  )}
                  {isMobile ? (
                    <MobileSignUpButton
                      service={Provider.Microsoft}
                      href={signUpPath({
                        provider: Provider.Microsoft,
                        to: webPath(dashboardPath(), router.locale),
                        hiddenPasswordProvider: 1,
                      })}
                    />
                  ) : (
                    <SignUpButton
                      variant="signup"
                      service={Provider.Microsoft}
                      isServiceOnly
                      href={signUpPath({
                        provider: Provider.Microsoft,
                        to: webPath(dashboardPath(), router.locale),
                        hiddenPasswordProvider: 1,
                      })}
                    />
                  )}
                </SignUpButtonContainer>
                <SignUpOtherContainer>
                  <MailSignUpButton
                    href={signUpPath({
                      to: webPath(dashboardPath(), router.locale),
                    })}
                  >
                    {t('email signup')}
                  </MailSignUpButton>
                  <MailSignUpButton
                    onClick={props.onClickInquiry}
                    href={signUpPath({
                      to: webPath(enterpriseInquiryPath(), router.locale),
                    })}
                  >
                    {t('inquiry team')}
                  </MailSignUpButton>
                </SignUpOtherContainer>
              </ButtonListItem>
            </ButtonList>
          </>
        ) : (
          <ButtonList>
            <LoggedInButtonListItem>
              <DashboardButton
                size="large"
                href={dashboardPath()}
                color="primary"
              >
                {t('Dashboard')}
              </DashboardButton>
              <DownloadButton
                size="large"
                href={downloadsPath()}
                color="primary"
                variant="outlined"
              >
                {t('Download')}
              </DownloadButton>
            </LoggedInButtonListItem>
          </ButtonList>
        ))}
      {props.image && (
        <MainImageContainer>
          <MainImage
            src={props.image.url}
            width={props.image.width || 600}
            height={props.image.height || 400}
            alt={props.title}
            dpr={2}
          />
        </MainImageContainer>
      )}
      {props.children}
    </ServiceMainVisual>
  );
}

const ServiceMainVisual = styled(BackgroundContainer)`
  text-align: center;
  padding: ${spacing(32)} 0 ${spacing(32)};
  z-index: 0;
  ${mediaMax.tablet} {
    padding: 40px 40px 80px;
  }
  ${mediaMax.tablet} {
    padding: 24px 0 80px;
  }
  ${mediaMax.smartphone} {
    padding: 40px 0 40px;
  }
`;
const Title = styled.h1<{ size: 'large' | 'medium' }>`
  display: inline-block;
  font-size: ${(props) => fontSize(props.size === 'large' ? 48 : 32)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(24)};
  letter-spacing: -0.04em;

  ${mediaMax.tablet} {
    padding: 0 ${spacing(20)};
    font-size: 6vw;
  }
`;
const Description = styled.p<{ size: 'large' | 'medium' }>`
  font-size: ${(props) => fontSize(props.size === 'large' ? 18 : 16)};
  /* letter-spacing: -0.02em; */
  max-width: 720px;
  margin: 0 auto ${spacing(24)};

  ${mediaMax.tablet} {
    font-size: 3vw;
    margin-bottom: ${spacing(20)};
  }

  br {
    display: none;

    ${mediaMax.tablet} {
      display: block;
    }
  }
  ${mediaMax.tablet} {
    max-width: 100%;
    margin: 0 ${spacing(12)} ${spacing(24)};
    padding: 0 ${spacing(20)};
    font-size: ${fontSize(14)};
  }
`;
const ButtonList = styled.div`
  margin-bottom: ${spacing(40)};
  position: relative;

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(32)};
  }
`;
const ButtonListItem = styled.div`
  display: inline-block;
  margin-bottom: ${spacing(20)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
  }
`;
const LoggedInButtonListItem = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing(20)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
    flex-direction: column;
    padding: 0 ${spacing(12)};
  }
`;
const MailSignUpButton = styled(Link)`
  color: ${black} !important;
  display: block;
  border-radius: ${borderRadius(6)};
  padding: ${spacing(6)} ${spacing(12)};
  font-size: ${fontSize(13)};
  text-decoration: underline;
`;
const SignUpButtonContainer = styled.div`
  display: flex;
  margin-bottom: ${spacing(16)};
`;
const SignUpButton = styled(LoginButton)`
  font-family: Gilroy, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji',
    'Segoe UI Symbol';
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizelegibility;
  span {
    font-size: ${fontSize(14)};
  }

  margin-right: ${spacing(16)};
  :last-of-type {
    margin-right: 0;
  }
`;
const MobileSignUpButton = styled(SmallSignUpButton)`
  margin-right: ${spacing(12)};
  span {
    color: ${black};
    letter-spacing: 0em;
    font-size: ${fontSize(13)};
  }
  :last-of-type {
    margin-right: 0;
  }
`;
const SignUpOtherContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${spacing(12)};
  ${mediaMax.tablet} {
    flex-direction: column;
  }
`;
const NewMark = styled.div`
  background-color: ${primary};
  margin-right: ${spacing(8)};
  color: ${whiteFix};
  font-size: ${fontSize(11)};
  font-weight: ${fontWeight('bold')};
  padding: 0 ${spacing(4)};
  border-radius: ${borderRadius(3)};
`;
const NewTitle = styled(Link)`
  margin-right: ${spacing(8)};
  color: ${black};
`;
const NewLinkIcon = styled(Icon)`
  color: ${gray01};
  width: 18px !important;
  height: 18px !important;
  margin-right: ${spacing(4)};
`;
const NewContent = styled.div`
  background-color: ${gray04};
  display: flex;
  align-items: center;
  width: fit-content;
  margin: 0 auto;
  padding: ${spacing(4)};
  border-radius: ${borderRadius(4)};
  margin-bottom: ${spacing(24)};
`;

const SignUpText = styled.div`
  color: ${gray01};
  margin-bottom: ${spacing(8)};
  font-size: ${fontSize(12)};
`;
const MainImageContainer = styled.div`
  padding: 0 ${spacing(12)};
`;
const MainImage = styled(Image)`
  ${mediaMax.tablet} {
    width: 100%;
    height: auto;
  }
`;

const DashboardButton = styled(Button)`
  min-width: 240px;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const DownloadButton = styled(Button)`
  min-width: 240px;
  margin-left: ${spacing(16)};
  :hover {
    color: ${whiteFix};
    background-color: ${primary};
  }
  ${mediaMax.tablet} {
    width: 100%;
    margin-left: 0;
    margin-top: ${spacing(16)};
  }
`;
